import { memo, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";

import Button from "common/core/button";
import CoreError from "common/core/error_message";
import LoadingIndicator from "common/core/loading_indicator/small";
import { exchangeToken } from "redux/actions/authentication";
import { useIsAuthenticated } from "common/authentication";
import { SERVER_NOT_FOUND, UNAUTHORIZED_ERROR } from "errors/server";
import { useSelector, useDispatch } from "redux/util";
import { useViewer } from "util/viewer_wrapper";
import { TRANSACTION_PATH } from "util/routes";

import Styles from "./account.module.scss";

function useLoginAndVerifyWithURLParams() {
  const dispatch = useDispatch();
  const { refetch } = useViewer();
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const email = searchParams.get("email")!;
    // deprecated: `code` has been renamed to `s_key`, but keeping here for
    // backwards compat until the backend has purged all refs to `code`
    const secret = searchParams.get("s_key") || searchParams.get("code")!;

    // When the component mounts fire off request w/ email & code
    // to retrieve an access token and implicitly verify the account.
    dispatch(exchangeToken({ email, secret, skipTokenStore: false })).then(refetch);
  }, []);
}

function Verified() {
  const navigate = useNavigate();
  return (
    <div className={Styles.verificationContainer}>
      <FormattedMessage
        id="f62a03f7-39cb-4142-aca9-1a3abc8955e9"
        defaultMessage="Your account is now verified!"
        tagName="h1"
      />
      <FormattedMessage
        id="3c6c1d68-628b-4149-9b84-95c3491c6908"
        defaultMessage="You have successfully verified your email address."
        tagName="p"
      />
      <Button
        buttonSize="large"
        buttonColor="action"
        variant="primary"
        onClick={() => navigate(TRANSACTION_PATH)}
      >
        <FormattedMessage
          id="65fd5569-1f82-4674-a0bb-32c9a5093741"
          defaultMessage="Continue to dashboard"
        />
      </Button>
    </div>
  );
}

function VerifyEmail() {
  const isAuthenticated = useIsAuthenticated();
  const { pending, error } = useSelector((state) => state.authentication);
  useLoginAndVerifyWithURLParams();

  if (!pending && isAuthenticated) {
    return <Verified />;
  }

  const navigateRootError =
    // Invaid email/secret
    error.type === UNAUTHORIZED_ERROR ||
    // User has already consumed their authentication token and set a password. Their link should now serve
    // as an entry point to the site, but should not authorizen them; they should get that from the login page.
    error.type === SERVER_NOT_FOUND;

  if (error.type && !navigateRootError) {
    return (
      <div className={Styles.verificationContainer}>
        <FormattedMessage
          id="90d5b4ab-f945-427a-a929-e71439ba791f"
          defaultMessage="Unknown error"
          tagName="h1"
        />
        <p>
          <CoreError error={error} />
        </p>
      </div>
    );
  }

  return (
    <div className={Styles.verificationContainer}>
      <FormattedMessage
        id="61d33055-b8aa-405b-a462-07b110d63bde"
        defaultMessage="Email verification"
        tagName="h1"
      />
      <FormattedMessage
        id="6f9a99d7-04d4-4b5c-b8df-1b0b88496e34"
        defaultMessage="Processing... {indicator}"
        values={{ indicator: <LoadingIndicator inline /> }}
        tagName="p"
      />
      {navigateRootError && <Navigate to="/" />}
    </div>
  );
}

export default memo(VerifyEmail);
